/**
 * PhoneSalesList — R7a (2026-05-18).
 *
 * List page for sales_activities rows of type='phone_sale'. Reuses the
 * get_phone_sales_report RPC which is PII-gated at the DB layer
 * (admin/manager only). For the non-PII "what did I log this week" use
 * case for rank-and-file reps, this page is hidden from staff role
 * until we add a non-PII variant — track for follow-up. For now staff
 * still sees the navigation entry but the list will error if they
 * try to load it.
 *
 * Includes a primary "Log a phone sale" CTA and inline cancel action.
 */

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPhoneSalesReport,
  cancelPhoneSale,
  type PhoneSalesReportRow,
  type SalesActivityReportFilter,
} from "../../services/salesActivities.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
} from "../../services/reportsSuite.service";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import { PlusIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): SalesActivityReportFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  repPositionId: f.repPositionId,
  page,
  limit,
});

const PhoneSalesList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.orgId, filters.repPositionId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["phone-sales-list", filters, page],
    queryFn: () => getPhoneSalesReport(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: any) => r.rep_position_id)
            .filter((id: any): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["phone-sales-list-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "—";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const handleCancel = async (row: PhoneSalesReportRow) => {
    if (
      !confirm(
        `Cancel this phone sale? It will be soft-cancelled (excluded from reports) but kept in the audit trail.`,
      )
    ) {
      return;
    }
    setCancellingId(row.activity_id);
    setCancelError(null);
    try {
      await cancelPhoneSale(row.activity_id);
      await queryClient.invalidateQueries({ queryKey: ["phone-sales-list"] });
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : "Could not cancel.");
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Phone Sales</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sales logged by reps over the phone. PII-restricted — admins and
            managers see everything; reps see rows they created or that credit
            their position.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/sales-activities/phone-sales/new")}
          className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Log a phone sale
        </button>
      </div>

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showRep={true}
      />

      {cancelError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {cancelError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Rep</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Member #</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No phone sales yet. Click "Log a phone sale" to record one.
                </td>
              </tr>
            )}
            {rows.map((r: any) => (
              <tr key={r.activity_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  <Link
                    to={`/sales-activities/phone-sales/${r.activity_id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {formatDate(r.created_at)}
                  </Link>
                </td>
                <td className="px-3 py-2">{repDisplay(r.rep_position_id)}</td>
                <td className="px-3 py-2">
                  {[r.contact_first_name, r.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </td>
                <td className="px-3 py-2">{r.member_number || "—"}</td>
                <td className="px-3 py-2">{r.status}</td>
                <td className="px-3 py-2 max-w-xs truncate">{r.notes || ""}</td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "cancelled" && (
                    <button
                      type="button"
                      onClick={() => handleCancel(r)}
                      disabled={cancellingId === r.activity_id}
                      className="text-xs text-red-600 underline hover:text-red-800 disabled:opacity-50"
                    >
                      {cancellingId === r.activity_id
                        ? "Cancelling…"
                        : "Cancel"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneSalesList;
